import {PropsWithChildren, ReactElement, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {AccountService} from "../services/AccountService";
import {Tooltip} from "@mantine/core";
import {authorityGuardDevStore} from "./authorityGuardDevStore";

type AuthorityGuardProps = PropsWithChildren<{
    authority?: string[];
}>;

const AuthorityGuard = ({ authority = [], children }: AuthorityGuardProps): ReactElement | null => {
    const hasAuth = AccountService.hasAuthority(authority);
    const [devEnabled, setDevEnabled] = useState(() =>
        import.meta.env.DEV ? authorityGuardDevStore.isEnabled() : false
    );

    useLayoutEffect(() => {
        if (!import.meta.env.DEV) return;
        return authorityGuardDevStore.subscribe(() => setDevEnabled(authorityGuardDevStore.isEnabled()));
    }, []);

    if (import.meta.env.DEV && devEnabled) {
        return (
            <AuthorityGuardDevOverlay authority={authority} hasAuth={hasAuth}>
                {children}
            </AuthorityGuardDevOverlay>
        );
    }

    if (!hasAuth) {
        return null;
    }
    return <>{children}</>;
};

function AuthorityGuardDevOverlay({ authority, hasAuth, children }: {
    authority: string[];
    hasAuth: boolean;
    children: React.ReactNode;
}): ReactElement {
    const spanRef = useRef<HTMLSpanElement>(null);
    const [rect, setRect] = useState<{ left: number; top: number; width: number; height: number; zIndex: number } | null>(null);
    const color = hasAuth ? '#22c55e' : '#ef4444';
    const label = authority.length ? authority.join(' | ') : '—';

    useLayoutEffect(() => {
        const compute = () => {
            const el = spanRef.current;
            if (!el) return;
            const children = Array.from(el.children);
            if (children.length === 0) return;

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            let maxZ = 0;
            for (const child of children) {
                const r = child.getBoundingClientRect();
                // Skip invisible elements (e.g. Tooltip hidden wrappers)
                if (r.width === 0 && r.height === 0) continue;
                minX = Math.min(minX, r.left);
                minY = Math.min(minY, r.top);
                maxX = Math.max(maxX, r.right);
                maxY = Math.max(maxY, r.bottom);
                const z = parseInt(window.getComputedStyle(child).zIndex, 10);
                if (!isNaN(z)) maxZ = Math.max(maxZ, z);
            }

            const w = maxX - minX;
            const h = maxY - minY;
            // Skip overlay for structural/page-level guards (> 40% of viewport)
            // if (w > window.innerWidth * 0.4 || h > window.innerHeight * 0.4) {
            //     setRect(null);
            //     return;
            // }

            setRect({ left: minX, top: minY, width: w, height: h, zIndex: maxZ });
        };

        compute();

        const ro = new ResizeObserver(compute);
        document.addEventListener('scroll', compute, true);
        if (spanRef.current?.parentElement) ro.observe(spanRef.current.parentElement);

        return () => {
            ro.disconnect();
            document.removeEventListener('scroll', compute, true);
        };
    }, [children]);

    return (
        <>
            <span ref={spanRef} style={{ display: 'contents' }}>
                {children}
            </span>
            {rect && createPortal(
                <>
                    {/* Visual border — pointer-events none so clicks pass through */}
                    <div style={{
                        position: 'fixed',
                        left: rect.left - 3,
                        top: rect.top - 3,
                        width: rect.width + 6,
                        height: rect.height + 6,
                        outline: `2px dashed ${color}`,
                        borderRadius: 4,
                        pointerEvents: 'none',
                        zIndex: rect.zIndex + 1,
                    }} />
                    {/* Tiny dot for tooltip — does not block clicks on children */}
                    <Tooltip label={label} withArrow position="top">
                        <div style={{
                            position: 'fixed',
                            left: rect.left + rect.width - 4,
                            top: rect.top - 6,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: color,
                            cursor: 'help',
                            zIndex: rect.zIndex + 2,
                        }} />
                    </Tooltip>
                </>,
                document.body
            )}
        </>
    );
}

export default AuthorityGuard;
